import { AppLocale } from "@nativecall/shared";

export type LegalDocumentKind = "privacy" | "terms" | "support";

type LegalSection = {
  heading: string;
  body: string;
};

type LegalDocument = {
  title: string;
  intro: string;
  sections: LegalSection[];
};

const defaultSiteBaseUrl = "https://nativecall.pages.dev";
const siteBaseUrl =
  process.env.EXPO_PUBLIC_SITE_BASE_URL?.trim() || defaultSiteBaseUrl;

export const legalLinks = {
  siteBaseUrl,
  privacyUrl: `${siteBaseUrl}/privacy`,
  termsUrl: `${siteBaseUrl}/terms`,
  supportUrl: `${siteBaseUrl}/support`,
  supportEmail:
    process.env.EXPO_PUBLIC_SUPPORT_EMAIL?.trim() || "kaiwajapanese@gmail.com",
};

const documentsByLocale: Record<AppLocale, Record<LegalDocumentKind, LegalDocument>> = {
  en: {
    privacy: {
      title: "Privacy Policy",
      intro:
        "NativeCall processes voice and transcript data so translated phone calls can work in real time.",
      sections: [
        {
          heading: "What we collect",
          body:
            "We collect the account details you provide, the phone number you dial, live microphone audio while a call is active, translated transcript segments, and call usage data such as timestamps, duration, and call status.",
        },
        {
          heading: "How we use it",
          body:
            "We use that data to connect translated calls, generate transcripts, meter credit usage, troubleshoot failed calls, and respond to support or safety issues.",
        },
        {
          heading: "Third-party processing",
          body:
            "Call audio, phone routing data, and transcripts are processed by service providers that power the product, including Twilio for telephony and OpenAI for real-time speech translation.",
        },
        {
          heading: "Retention and deletion",
          body:
            "Transcript history is stored in your account so you can review past calls. You can request full account deletion inside the app, which removes your account record and associated call history unless we are legally required to keep limited records.",
        },
        {
          heading: "Safety",
          body:
            "NativeCall is not for emergency use. AI translation can be delayed, incomplete, or inaccurate, so you should not rely on it where mistakes could cause harm.",
        },
      ],
    },
    terms: {
      title: "Terms of Use",
      intro:
        "NativeCall is a translated calling service for ordinary conversations, not a guaranteed or regulated communication channel.",
      sections: [
        {
          heading: "Acceptable use",
          body:
            "You may use NativeCall only for lawful calls that you are authorized to place. Anonymous, prank, deceptive, abusive, or harassing calls are not allowed.",
        },
        {
          heading: "Translation limits",
          body:
            "The service uses AI-generated speech translation. Output may be delayed, incomplete, or wrong. You remain responsible for what is said and how you use the service.",
        },
        {
          heading: "Emergency restriction",
          body:
            "Do not use NativeCall for emergency, medical, legal, safety-critical, or other urgent situations where errors or delay could cause harm.",
        },
        {
          heading: "Billing",
          body:
            "Calls consume prepaid credits based on usage. Pricing, credit packs, and billing flows may change by region or app version.",
        },
        {
          heading: "Account controls",
          body:
            "You can end calls at any time. You can also request account deletion inside the app. Deleting your account removes access to stored transcript history.",
        },
      ],
    },
    support: {
      title: "Support",
      intro:
        "Contact us if you need help with NativeCall.",
      sections: [
        {
          heading: "Contact",
          body: `Support email: ${legalLinks.supportEmail}`,
        },
        {
          heading: "What we can help with",
          body:
            "Account access, billing questions, transcript deletion requests, translation quality issues, and blocked or failed calls.",
        },
      ],
    },
  },
  zh: {
    privacy: {
      title: "隱私權政策",
      intro:
        "NativeCall 會處理語音與逐字稿資料，才能即時完成雙向通話翻譯。",
      sections: [
        {
          heading: "我們收集什麼",
          body:
            "我們會收集你提供的帳戶資料、撥打的電話號碼、通話進行時的麥克風語音、翻譯逐字稿片段，以及時間、通話長度與狀態等使用資料。",
        },
        {
          heading: "如何使用",
          body:
            "這些資料用於建立翻譯通話、產生逐字稿、計算點數、排查失敗通話，以及處理客服或安全相關問題。",
        },
        {
          heading: "第三方處理",
          body:
            "通話語音、電話路由資料與逐字稿會由提供服務的第三方處理，包括 Twilio 的電話能力，以及 OpenAI 的即時語音翻譯能力。",
        },
        {
          heading: "保存與刪除",
          body:
            "逐字稿會保存在你的帳戶中，方便回看歷史通話。你可以在 App 內申請刪除帳戶；除法律必須保存的有限資料外，帳戶與關聯通話紀錄都會一併移除。",
        },
        {
          heading: "安全提醒",
          body:
            "NativeCall 不是緊急用途服務。AI 翻譯可能延遲、不完整或不準確，不應用於任何錯誤可能造成傷害的情境。",
        },
      ],
    },
    terms: {
      title: "使用條款",
      intro:
        "NativeCall 是一般用途的即時翻譯通話服務，並非保證無誤或受監管的通訊管道。",
      sections: [
        {
          heading: "允許用途",
          body:
            "你只能在合法且有權撥打的情況下使用 NativeCall。禁止匿名、惡作劇、欺騙、騷擾或濫用通話。",
        },
        {
          heading: "翻譯限制",
          body:
            "本服務使用 AI 產生語音翻譯。輸出可能延遲、不完整或錯誤；你仍需對實際對話內容與使用方式負責。",
        },
        {
          heading: "緊急用途限制",
          body:
            "請勿將 NativeCall 用於緊急、醫療、法律、安全關鍵或任何錯誤與延遲可能造成傷害的情境。",
        },
        {
          heading: "計費",
          body:
            "通話會依使用量消耗預付點數。價格、點數組合與計費方式可能依地區或版本調整。",
        },
        {
          heading: "帳戶控制",
          body:
            "你可以隨時結束通話，也可以在 App 內申請刪除帳戶。刪除帳戶後，已保存的逐字稿歷史將一併移除。",
        },
      ],
    },
    support: {
      title: "客服支援",
      intro:
        "需要協助時，歡迎直接與我們聯絡。",
      sections: [
        {
          heading: "聯絡方式",
          body: `客服信箱：${legalLinks.supportEmail}`,
        },
        {
          heading: "支援範圍",
          body:
            "帳戶存取、計費問題、逐字稿刪除請求、翻譯品質問題，以及被阻擋或失敗的通話。",
        },
      ],
    },
  },
};

export function getLegalDocument(locale: AppLocale, kind: LegalDocumentKind) {
  return documentsByLocale[locale][kind];
}
